import { useState, useCallback, useEffect } from "react";
import { PROTOCOL_TYPES, USER_ROLES } from "../../Constants/AppConstants";
import { useAuth } from "../../Context/AuthProvider";
import { useTradeBlotterOrderStore } from "../../Store/tradeBlotterOrderStore";
import { jwtDecode } from "jwt-decode";
import { submitTrade } from '../../http/api';
import { useNavigate } from "react-router-dom";
export const useTradeEntry = ({
  onTradeAdded,
  onClose,
  prefillData = null,
  lockedFields = [],
}) => {
  const { token,userId } = useAuth();
  const decodedToken = jwtDecode(token);
  const tokenRole = decodedToken.role;

  // State management
  const [error, setError] = useState(null);
  const [lastErrorField, setLastErrorField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Store hooks
  const {
    tradeEntryData,
    setTradeEntryData,
    fieldErrors,
    setFieldErrors,
    resetTradeEntryData,
    bondDetails,
    cusipError,
    addTrade,
    accountIdentifiers,
    clearCounterparty, // <-- new helper from store
    clearFieldError, // <-- ADDED: helper to clear a specific field error
  } = useTradeBlotterOrderStore();
  const navigate = useNavigate();

  // Apply prefill once on mount / prefillData change
  useEffect(() => {
    if (prefillData) {
      // Inject account into identifiers if missing (so disabled select shows value)
      if (prefillData.account) {
        const list = accountIdentifiers || [];
        if (!list.includes(prefillData.account)) {
          // minimal setter (avoid adding method to store)
          setTradeEntryData({}); // noop to ensure reactivity
          // direct add by merging after next tick
          setTimeout(() => {
            // Safe merge approach
            try {
              useTradeBlotterOrderStore.setState((state) => ({
                accountIdentifiers: [...new Set([...state.accountIdentifiers, prefillData.account])]
              }));
            } catch {}
          }, 0);
        }
      }
      setTradeEntryData({
        cusip: prefillData.cusip ?? '',
        side: prefillData.side ?? '',
        tradeQuantity: prefillData.tradeQuantity ?? prefillData.qty ?? '',
        tradePrice: prefillData.tradePrice ?? prefillData.price ?? '',
        // pmId: prefillData.pmId ?? null,
        userId: userId ?? null,
        // pm: prefillData.pm ?? '',
        account: prefillData.account ?? '',
        orderId: prefillData.orderId ?? null,
        bondInventoryId: prefillData.bondInventoryId ?? null,
      });

      // NEW: clear field errors for any fields populated by the prefill
      try {
        const populatedFields = [
          "cusip",
          "side",
          "tradeQuantity",
          "tradePrice",
          // "pmId",
          "account",
          "orderId",
          "bondInventoryId",
          "executionDateTime",
          "settlementDate",
          "counterPartyId",
          "principalAmount",
          "accruedInterest",
          "estimatedTotal",
        ];
        populatedFields.forEach((field) => {
          const val = prefillData[field];
          if (val !== undefined && val !== null && String(val).toString().trim() !== "") {
            clearFieldError(field);
          }
        });
      } catch (e) {
        // swallow - non-critical
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Clear all while keeping locked fields
  const clearDataInAllTabs = useCallback(() => {
    const preserved = {};
    if (prefillData) {
      lockedFields.forEach(f => {
        // if (f === 'pmId' && prefillData.pmId != null) {
        //   preserved.pmId = prefillData.pmId;
        //   preserved.pm = prefillData.pm;
        // } 
        if (prefillData[f] !== undefined) {
          preserved[f] = prefillData[f];
        }
      });
    }
    resetTradeEntryData();
    if (Object.keys(preserved).length) {
      setTradeEntryData(preserved);
    }
    setShowConfirmation(false);
    setError(null);
  }, [resetTradeEntryData, prefillData, lockedFields, setTradeEntryData]);

  // Hard reset (used when closing via X) - do NOT preserve anything
  const resetAllUnconditional = useCallback(() => {
    resetTradeEntryData();
    setShowConfirmation(false);
    setError(null);
  }, [resetTradeEntryData]);

  // Handle clear all action
  const handleClearAll = useCallback(() => {
    clearDataInAllTabs();
  }, [clearDataInAllTabs]);

  // Helper functions
  const trimObjectValues = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = typeof obj[key] === "string" ? obj[key].trim() : obj[key];
      return acc;
    }, {});
  };

  // add helper to pick allowed fields
  const pick = (obj, keys) =>
    keys.reduce((acc, k) => {
      if (obj[k] !== undefined) acc[k] = obj[k];
      return acc;
    }, {});

  const TRADE_FIELDS_TO_SEND = [
    "cusip",
    "tradeQuantity",
    "tradePrice",
    "side",
    "executionDateTime",
    "counterPartyId",
    "settlementDate",
    "account",
    // "pmId",
    "comments",
    "tradeTypeCode",
    "accruedInterest",
    "principalAmount",
    "estimatedTotal",
    "bondInventoryId",
    "secMasterId",
  ];

  // Main submit handler
  const handleSubmit = async () => {
    // Always validate and show errors, even if submit is attempted
    const trimmedData = trimObjectValues(tradeEntryData);
    
    // Explicitly require CUSIP to be at least 9 characters at submit time.
    // This prevents submitting when user shortens a previously validated CUSIP.
    if (!trimmedData.cusip || String(trimmedData.cusip).trim().length < 9) {
      setFieldErrors(prev => ({ ...prev, cusip: true }));
      setModalError("CUSIP must be at least 9 characters", 'cusip');
      return false;
    }
    
    // Check if CUSIP is valid first
    if (!bondDetails || cusipError) {
      setFieldErrors(prev => ({ ...prev, cusip: true }));
      setModalError("Please enter a valid CUSIP", 'cusip');
      return false;
    }
    
    // Define all required fields including financial fields
    const requiredFields = [
      "account",
      "cusip", 
      "side",
      "tradeQuantity",
      "tradePrice",
      "executionDateTime",
      "counterPartyId",
      "settlementDate",
      "principalAmount",
      "accruedInterest", 
      "estimatedTotal"
    ];

    // Always validate all required fields and show visual feedback
    let newErrors = {};
    let hasErrors = false;
    
    requiredFields.forEach((field) => {
      const value = trimmedData[field];
      const isEmpty = !value || value.toString().trim() === "" || value === null || value === undefined;
      
      if (isEmpty) {
        newErrors[field] = true;
        hasErrors = true;
      }
    });

    // Additional validation for numeric fields
    if (trimmedData.tradeQuantity) {
      const qty = Number(trimmedData.tradeQuantity);
      if (isNaN(qty) || qty <= 0) {
        newErrors.tradeQuantity = true;
        hasErrors = true;
      }
    } else if (trimmedData.tradeQuantity === '' || trimmedData.tradeQuantity === null || trimmedData.tradeQuantity === undefined) {
      newErrors.tradeQuantity = true;
      hasErrors = true;
    }

    if (trimmedData.tradePrice) {
      const price = Number(trimmedData.tradePrice);
      if (isNaN(price) || price <= 0) {
        newErrors.tradePrice = true;
        hasErrors = true;
      }
    } else if (trimmedData.tradePrice === '' || trimmedData.tradePrice === null || trimmedData.tradePrice === undefined) {
      newErrors.tradePrice = true;
      hasErrors = true;
    }

    // Validate financial fields
    if (trimmedData.principalAmount) {
      const principal = Number(trimmedData.principalAmount);
      if (isNaN(principal) || principal <= 0) {
        newErrors.principalAmount = true;
        hasErrors = true;
      }
    } else if (trimmedData.principalAmount === '' || trimmedData.principalAmount === null || trimmedData.principalAmount === undefined) {
      newErrors.principalAmount = true;
      hasErrors = true;
    }

    if (trimmedData.accruedInterest) {
      const accrued = Number(trimmedData.accruedInterest);
      if (isNaN(accrued)) {
        newErrors.accruedInterest = true;
        hasErrors = true;
      }
    } else if (trimmedData.accruedInterest === '' || trimmedData.accruedInterest === null || trimmedData.accruedInterest === undefined) {
      newErrors.accruedInterest = true;
      hasErrors = true;
    }

    if (trimmedData.estimatedTotal) {
      const total = Number(trimmedData.estimatedTotal);
      if (isNaN(total) || total <= 0) {
        newErrors.estimatedTotal = true;
        hasErrors = true;
      }
    } else if (trimmedData.estimatedTotal === '' || trimmedData.estimatedTotal === null || trimmedData.estimatedTotal === undefined) {
      newErrors.estimatedTotal = true;
      hasErrors = true;
    }

    // Validate counterparty selection
    if (!trimmedData.counterPartyId || trimmedData.counterPartyId === "") {
      newErrors.counterPartyId = true;
      hasErrors = true;
    }

    // Always set field errors to show red borders
    setFieldErrors(newErrors);

    // If there are validation errors, show message and prevent submission
    if (hasErrors) {
      // Find the first error field to focus on
      const firstErrorField = requiredFields.find(field => newErrors[field]);
      const fieldDisplayNames = {
        account: "Account",
        cusip: "CUSIP",
        side: "Side",
        tradeQuantity: "Trade Quantity", 
        tradePrice: "Trade Price",
        executionDateTime: "Execution Date Time",
        counterPartyId: "Counterparty",
        settlementDate: "Settlement Date",
        principalAmount: "Principal Amount",
        accruedInterest: "Accrued Interest",
        estimatedTotal: "Estimated Total",
        // pmId: "Portfolio Manager"
      };
      
      const displayName = fieldDisplayNames[firstErrorField] || firstErrorField;
      // setModalError(`Please fill in all required fields. Missing: ${displayName}`, firstErrorField);
      return false;
    }

    // If validation passes, proceed with submission
    setLoading(true);
    setError(null);

    const loadingTimeout = setTimeout(() => {
      setError("Request failed to initiate. Please try again.");
      setLoading(false);
    }, 15000);

    // Map form data to backend keys (already camelCase)
    const parsedCounterpartyId = (() => {
      if (trimmedData.counterPartyId === undefined || trimmedData.counterPartyId === null || trimmedData.counterPartyId === "") return null;
      const n = Number(trimmedData.counterPartyId);
      return Number.isFinite(n) ? n : null;
    })();

    const dataToSubmit = {
      cusip: trimmedData.cusip,
      tradeQuantity: Number(trimmedData.tradeQuantity || trimmedData.qty),
      tradePrice: Number(trimmedData.tradePrice || trimmedData.price),
      side: trimmedData.side,
      executionDateTime: trimmedData.executionDateTime,
      counterPartyId: parsedCounterpartyId,
      settlementDate: trimmedData.settlementDate,
      account: trimmedData.account,
      // pmId: trimmedData.pmId ? Number(trimmedData.pmId) : null,
      userId: userId ? Number(userId) : null,
      comments: trimmedData.comments,
      tradeTypeCode: Number(trimmedData.tradeTypeCode) || 1,
      accruedInterest: Number(trimmedData.accruedInterest || trimmedData.accrued_interest) || 0,
      principalAmount: Number(trimmedData.principalAmount || trimmedData.principal_amount) || 0,
      estimatedTotal: Number(trimmedData.estimatedTotal || trimmedData.estimated_total) || 0,
      bondInventoryId: Number(trimmedData.bondInventoryId),
      secMasterId: Number(trimmedData.secMasterId),
      action: 0, // 0 for CREATE
      tradeId: null, // null for create
      orderId: Number(trimmedData.orderId) || null, // null for create
    };

    try {
      console.log(dataToSubmit)
      const response = await submitTrade(token, dataToSubmit);
      
      // If backend indicates failure, handle specific counterparty-not-found case
      if (response && response.success === false) {
        const msg = response.message || "";
        const counterpartyNotFound = /\bcounter\s*-?\s*party\s*not\s*found\b/i.test(msg) || /counterparty\s*not\s*found/i.test(msg);
        if (counterpartyNotFound) {
          // Clear selection via store helper so UI/listeners are reliably notified
          clearCounterparty();
          // Optionally set an error flag so field shows validation state
          setFieldErrors(prev => ({ ...prev, counterPartyId: true }));
        }
        // Surface error to user
        setError(msg || "Failed to submit trade.");
        return false;
      }
      
      // Assuming response includes the created trade data
      // const newTrade = {
      //   ...response,
      //   dateTime: new Date().toISOString(),
      // };

      // if (onTradeAdded) {
      //   onTradeAdded(newTrade);
      // }

      clearDataInAllTabs();
      navigate("/order?view=1&tab=0");
      // setShowConfirmation(true);
      
      // // Auto-close after showing confirmation
      // setTimeout(() => {
      //   setShowConfirmation(false);
      //   if (onClose) onClose();
      // }, 2000);
      if (onClose) onClose();

      return true;
    } catch (error) {
      console.error("Failed to submit trade:", error);
      setError(error.message || "Failed to submit trade. Please try again.");
      return false;
    } finally {
      clearTimeout(loadingTimeout);
      setLoading(false);
    }
  };

  // setModalError associates an error message with an optional field name
  const setModalError = (msg, fieldName = null) => {
    setError(msg);
    setLastErrorField(fieldName);
  };
  
  // clearModalError will clear the modal error only if the provided field
  // matches the lastErrorField (or if no field provided, clears unconditionally)
  const clearModalError = (fieldName = null) => {
    if (!fieldName || lastErrorField === fieldName) {
      setError(null);
      setLastErrorField(null);
    }
  };

  // NEW: whenever a field that previously had an error now has a non-empty value,
  // clear that specific field error and any modal error associated with it.
  useEffect(() => {
    if (!fieldErrors) return;
    Object.keys(fieldErrors).forEach((field) => {
      if (fieldErrors[field]) {
        const val = tradeEntryData ? tradeEntryData[field] : undefined;
        const hasValue =
          val !== undefined &&
          val !== null &&
          (typeof val !== "string" || val.toString().trim() !== "");
        if (hasValue) {
          clearFieldError(field);
          // clear modal-level error for this field if present
          try {
            // clearModalError is provided by this hook's return; call via closure if available
            if (typeof clearModalError === "function") {
              clearModalError(field);
            }
          } catch {}
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeEntryData, fieldErrors]);

  return {
    // State
    error,
    setModalError,
    clearModalError,
    setError,
    loading,
    setLoading,
    showConfirmation,
    setShowConfirmation,
    hasValidationErrors,
    setHasValidationErrors,
    // Store data
    tradeEntryData,
    setTradeEntryData,
    // Field errors
    fieldErrors,
    // Actions
    handleSubmit,
    handleClearAll: clearDataInAllTabs,
    clearDataInAllTabs,
    resetAllUnconditional, // <- expose hard reset
    // Token and role
    token,
    tokenRole,
  };
};